import React, { useState, useCallback, useMemo, useReducer } from 'react'
import PropTypes from 'prop-types'
import { Form, Field } from 'formik'

import { useModifierFormHelpers, useHandleSubmit } from '../use-modifier-form-helpers'
import Modal from '../../Modal/Modal'
import { ModifierBody } from '../ModifierBody/ModifierBody'
import { ItemQuantity } from '../ItemQuantity/ItemQuantity'
import { FractionalItemQuantity } from '../FractionalItemQuantity/FractionalItemQuantity'

import { ModifiersFooter } from '../ModifiersFooter/ModifiersFooter'
import { ModifiersHeader } from '../ModifiersHeader/ModifiersHeader'
import { LoadingModal } from '../LoadingModal/LoadingModal'
import { ErrorModal } from '../ErrorModal/ErrorModal'
import { RemoveItemButton } from '../RemoveItemButton/RemoveItemButton'
import { FormikProvider } from '../../FormikProvider/FormikProvider'
import { NestedModifiersProvider } from '../NestedModifiersProvider/NestedModifiersProvider'
import { PanelStack } from '../../PanelStack/PanelStack'
import { useCart } from '../../CartProvider/CartProvider'
import { PanelProvider } from '../../PanelStack/PanelProvider'
import { MenuItemDetails } from '../NestedModifiersProvider/Models/MenuItemDetails'
import { useRestaurant } from '../../RestaurantProvider/RestaurantProvider'

export const UpdateItemFormModal = ({
  updateCart,
  error,
  itemDetailsQuery,
  getItemDetails,
  getBasePrice,
  onClose,
  itemGuid,
  itemGroupGuid,
  selectionGuid,
  mayDelete,
  isUpsell = false
}) => {
  const { cartGuid } = useCart()
  const { restaurantInfo } = useRestaurant()

  const [modalError, setModalError] = useState(null)

  // Coerce from data to model
  const itemDetailsData = getItemDetails(itemDetailsQuery.data)
  const itemDetails = useMemo(() => itemDetailsData ? new MenuItemDetails(itemDetailsData) : null, [itemDetailsData])
  const basePrice = getBasePrice(itemDetailsQuery.data)

  const restaurant = restaurantInfo.data && restaurantInfo.data.restaurant

  const {
    usesFractionalQuantity,
    fractionalQuantity,
    unitOfMeasure
  } = itemDetails || {}

  const {
    getComputedPrice,
    getModifierGroups,
    formikProps,
    getSelectedNames
  } = useModifierFormHelpers({
    itemDetails,
    itemGroupGuid,
    usesFractionalQuantity,
    fractionalQuantity
  })

  const handleSubmit = useHandleSubmit({
    itemDetails,
    restaurant,
    getComputedPrice,
    basePrice,
    cartGuid,
    itemGuid,
    itemGroupGuid,
    getModifierGroups,
    updateCart,
    selectionGuid
  })

  const getDisplayPrice = useCallback((values) => {
    if (usesFractionalQuantity) {
      const fractionalBasePrice = basePrice * values.fractionalQuantity
      const modifierPrice = getComputedPrice(values, values.quantity)

      return fractionalBasePrice + modifierPrice
    }

    return (getComputedPrice(values, values.quantity) + basePrice) * values.quantity
  }, [basePrice, getComputedPrice, usesFractionalQuantity])

  const [modalBody, setModalBodyFromModal] = useReducer(
    (state, modalComponent) => {
      if (modalComponent) {
        return modalComponent.modalBodyRef.current
      }
    },
    undefined
  )

  if (itemDetailsQuery.loading || !itemDetailsQuery.called || restaurantInfo.loading) {
    return <LoadingModal onClose={onClose} />
  }
  if (itemDetailsQuery.error || restaurantInfo.error) {
    return (
      <ErrorModal
        onClose={onClose}
        error={itemDetailsQuery.error}
        retry={itemDetailsQuery.refetch}
      />
    )
  }
  const quantityInputField = usesFractionalQuantity ? (
    <Field
      name='fractionalQuantity'
      component={FractionalItemQuantity}
      unitOfMeasure={unitOfMeasure}
      price={basePrice}
    />
  ) : (
    <Field
      name='quantity'
      component={ItemQuantity}
    />
  )

  return (
    <FormikProvider
      {...formikProps}
      onSubmit={handleSubmit}
    >
      <PanelProvider>
        <NestedModifiersProvider>
          <Modal
            header={<ModifiersHeader itemDetails={itemDetails} />}
            error={error || modalError}
            wrapper={children => <Form>{children}</Form>}
            onClose={onClose}
            footer={(
              <ModifiersFooter
                itemDetails={itemDetails}
                getDisplayPrice={getDisplayPrice}
                onClose={onClose}
                additionalText={isUpsell ? ' And Checkout' : ''}
              />
            )}
            ref={setModalBodyFromModal}
          >
            <PanelStack panelBody={modalBody}>
              {/* Children of PanelStack will not re-render according to the expected React tree pattern */}
              <ModifierBody
                itemDetails={itemDetails}
                specialRequestsConfig={restaurant.specialRequestsConfig}
                quantityInputField={quantityInputField}
                getSelectedNames={getSelectedNames}
                deleteButton={mayDelete && (
                  <RemoveItemButton
                    itemDetails={itemDetails}
                    updateError={setModalError}
                    handleDeleteParams={{
                      selectionGuid,
                      getComputedPrice,
                      basePrice
                    }}
                  />
                )}
              />
            </PanelStack>
          </Modal>
        </NestedModifiersProvider>
      </PanelProvider>
    </FormikProvider>
  )
}

const itemDetailsQueryShape = PropTypes.shape({
  loading: PropTypes.bool.isRequired,
  refetch: PropTypes.func,
  data: PropTypes.object,
  error: PropTypes.object
})

UpdateItemFormModal.propTypes = {
  updateCart: PropTypes.func.isRequired,
  error: PropTypes.object,
  itemDetailsQuery: itemDetailsQueryShape.isRequired,
  getItemDetails: PropTypes.func.isRequired,
  getBasePrice: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  itemGuid: PropTypes.string.isRequired,
  itemGroupGuid: PropTypes.string.isRequired,
  // for editing
  selectionGuid: PropTypes.string,
  mayDelete: PropTypes.bool,
  isUpsell: PropTypes.bool
}
